// Import formatPace from formatters.js
import { formatPace } from './formatters';
import { memoize, generateFormDataKey } from './memoize';
import { getPreRaceFuelState } from './preRaceFuel';

// Conversion constants
const MILES_TO_KM = 1.60934;
const KM_TO_MILES = 0.621371;
const METERS_TO_MILES = 0.000621371;
const METERS_TO_KM = 0.001; // 1 meter = 0.001 kilometers

/**
 * Converts distance between miles and kilometers
 * @param {number} distance - The distance to convert
 * @param {string} fromUnit - The unit to convert from ('miles' or 'kilometers')
 * @param {string} toUnit - The unit to convert to ('miles' or 'kilometers')
 * @returns {number} - The converted distance
 */
export const convertDistance = (distance, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return distance;
  
  if (fromUnit === 'miles' && toUnit === 'kilometers') {
    return distance * MILES_TO_KM;
  } else if (fromUnit === 'kilometers' && toUnit === 'miles') {
    return distance * KM_TO_MILES;
  }
  
  return distance;
};

/**
 * Normalizes aid stations to ensure consistent units
 * Converts all aid station locations to match the race distance unit
 * @param {Array} aidStations - Array of aid station objects
 * @param {string} raceUnit - The race distance unit ('miles' or 'kilometers')
 * @param {string} aidStationUnit - The aid station unit ('miles' or 'kilometers')
 * @returns {Array} - Normalized aid stations with locations in race units
 */
export const normalizeAidStations = (aidStations, raceUnit, aidStationUnit) => {
  if (!aidStations || !Array.isArray(aidStations)) {
    return [];
  }

  const unitsAreDifferent = raceUnit !== aidStationUnit;

  return aidStations
    .map((station, index) => {
      // Parse the location value
      let locationValue = typeof station.location === 'number' 
        ? station.location 
        : parseFloat(station.location);

      if (isNaN(locationValue)) {
        console.warn(`Aid station at index ${index} has invalid location:`, station.location);
        return null;
      }

      // Get original location and unit (for tracking)
      const originalLocationValue = station.originalLocation !== undefined
        ? (typeof station.originalLocation === 'number' ? station.originalLocation : parseFloat(station.originalLocation))
        : locationValue;

      const originalUnitValue = station.originalUnit || aidStationUnit;

      // Convert location to race units if needed
      let normalizedLocation = locationValue;
      if (unitsAreDifferent || originalUnitValue !== raceUnit) {
        normalizedLocation = convertDistance(originalLocationValue, originalUnitValue, raceUnit);
      }

      return {
        ...station,
        originalLocation: originalLocationValue,
        originalUnit: originalUnitValue,
        location: normalizedLocation, // Now in race units
        normalizedUnit: raceUnit
      };
    })
    .filter(station => station !== null)
    .sort((a, b) => a.location - b.location); // Sort by normalized location
};

/**
 * Calculates the number of gels needed based on distance and frequency
 * @param {number} distance - The distance in miles or kilometers
 * @param {number} frequency - How often to consume a gel
 * @param {string} distanceUnit - The unit of the distance ('miles' or 'kilometers')
 * @param {string} frequencyUnit - The unit of the frequency ('miles', 'kilometers', or 'hours')
 * @param {number} pace - The pace in minutes per mile (only needed if frequencyUnit is 'hours')
 * @returns {number} - The number of gels needed (rounded up)
 */
export const calculateGelsNeeded = (distance, frequency, distanceUnit, frequencyUnit, pace = null) => {
  // If frequency is in hours, convert to distance based on pace
  if (frequencyUnit === 'hours') {
    if (!pace) return 0;
    
    // Convert hours to miles based on pace (min/mile)
    const milesPerHour = 60 / pace;
    const distancePerFrequency = milesPerHour * frequency;
    
    // Convert to the appropriate distance unit
    frequency = distanceUnit === 'kilometers' 
      ? convertDistance(distancePerFrequency, 'miles', 'kilometers')
      : distancePerFrequency;
    
    frequencyUnit = distanceUnit;
  }
  
  // Ensure distance and frequency are in the same unit
  if (distanceUnit !== frequencyUnit) {
    if (distanceUnit === 'miles' && frequencyUnit === 'kilometers') {
      frequency = convertDistance(frequency, 'kilometers', 'miles');
    } else if (distanceUnit === 'kilometers' && frequencyUnit === 'miles') {
      frequency = convertDistance(frequency, 'miles', 'kilometers');
    }
  }
  
  // Calculate gels needed and round up
  // We subtract a small amount (0.001) to handle floating point precision issues
  // This ensures that if we need exactly N gels, we don't round up to N+1
  return Math.ceil((distance - 0.001) / frequency);
};

/**
 * Calculates the number of fuel units needed based on time and units per hour
 * @param {number} timeInHours - The time in hours
 * @param {number} unitsPerHour - Units consumed per hour
 * @returns {number} - The number of units needed (rounded up)
 */
export const calculateFuelUnitsNeeded = (timeInHours, unitsPerHour) => {
  return Math.ceil(timeInHours * unitsPerHour);
};

/**
 * Gets the display name for a fuel unit
 * @param {string} type - The type of fuel unit ('gel', 'drinkMix', 'custom')
 * @returns {string} - The display name for the unit
 */
export const getFuelUnitDisplayName = (type) => {
  switch(type) {
    case 'gel': return 'gel';
    case 'drinkMix': return 'scoop';
    case 'custom': return 'serving';
    default: return 'unit';
  }
};

/**
 * Gets the plural display name for a fuel unit
 * @param {string} type - The type of fuel unit ('gel', 'drinkMix', 'custom')
 * @returns {string} - The plural display name for the unit
 */
export const getFuelUnitDisplayNamePlural = (type) => {
  switch(type) {
    case 'gel': return 'gels';
    case 'drinkMix': return 'scoops';
    case 'custom': return 'servings';
    default: return 'units';
  }
};

/**
 * Formats a time of day based on hours and minutes
 * @param {number} hours - The hours (0-23)
 * @param {number} minutes - The minutes (0-59)
 * @returns {string} - The formatted time (e.g., "8:30 AM")
 */
export const formatTimeOfDay = (hours, minutes) => {
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
  const displayMinutes = minutes.toString().padStart(2, '0');
  return `${displayHours}:${displayMinutes} ${period}`;
};

/**
 * Calculates the time of day based on a start time and elapsed hours
 * @param {number} startHours - The start hour (1-12)
 * @param {number} startMinutes - The start minutes (0-59)
 * @param {string} startAMPM - "AM" or "PM"
 * @param {number} elapsedHours - The elapsed time in hours
 * @returns {string} - The formatted time of day
 */
export const calculateTimeOfDay = (startHours, startMinutes, startAMPM, elapsedHours) => {
  // Convert start time to 24-hour format
  // Ensure startHours and startMinutes are numbers (they might be strings from form inputs)
  const numericStartHours = parseInt(startHours, 10);
  const numericStartMinutes = parseInt(startMinutes, 10);
  
  let hours24 = numericStartHours;
  if (startAMPM === 'PM' && numericStartHours < 12) {
    hours24 += 12;
  } else if (startAMPM === 'AM' && numericStartHours === 12) {
    hours24 = 0;
  }
  
  // Calculate elapsed time
  const totalMinutes = hours24 * 60 + numericStartMinutes + elapsedHours * 60;
  
  // Convert back to hours and minutes
  const resultHours = Math.floor(totalMinutes / 60) % 24;
  const resultMinutes = Math.floor(totalMinutes % 60);
  
  return formatTimeOfDay(resultHours, resultMinutes);
};

/** Minimum “moving time” budget to avoid divide-by-zero (1 minute). */
const MIN_MOVING_HOURS = 1 / 60;

/**
 * Total planned stop at an aid (minutes). Finish row excluded.
 * Uses HH:MM-style fields `aidStopDurationHours` + `aidStopDurationMinutes`, or legacy `stopTimeMinutes`.
 * @param {Object} station
 * @returns {number}
 */
export const parseAidStopMinutes = (station) => {
  if (!station || station.isFinish) return 0;

  const hRaw = station.aidStopDurationHours;
  const mRaw = station.aidStopDurationMinutes;
  const hasH = hRaw !== undefined && hRaw !== '' && hRaw !== null;
  const hasM = mRaw !== undefined && mRaw !== '' && mRaw !== null;

  if (hasH || hasM) {
    const h = Math.max(0, parseInt(hRaw, 10) || 0);
    let mm = Math.max(0, parseInt(mRaw, 10) || 0);
    if (mm > 59) mm = 59;
    return h * 60 + mm;
  }

  const legacy = parseFloat(station.stopTimeMinutes);
  if (Number.isNaN(legacy) || legacy < 0) return 0;
  return legacy;
};

/** Format total minutes as H:MM for display (e.g. 120 → "2:00"). */
export const formatAidStopDurationHM = (totalMinutes) => {
  const n = Math.max(0, Math.round(Number(totalMinutes) || 0));
  if (n === 0) return '0:00';
  const h = Math.floor(n / 60);
  const mm = n % 60;
  return `${h}:${String(mm).padStart(2, '0')}`;
};

/**
 * Sum of planned aid stops in hours (stations in normalized race units).
 * @param {Array} normalizedAidStations
 * @returns {number}
 */
export const totalPlannedAidStopHours = (normalizedAidStations) => {
  if (!normalizedAidStations?.length) return 0;
  return normalizedAidStations.reduce((sum, s) => sum + parseAidStopMinutes(s) / 60, 0);
};

/**
 * Calculates the nutrition plan for the race (internal, non-memoized)
 * @param {Object} formData - The form data
 * @returns {Object} - The nutrition plan
 */
const calculateNutritionPlanInternal = (formData) => {
  // Extract relevant variables from formData
  const {
    distance: totalDistance,
    distanceUnit,
    aidStations = [],
    nutritionType = 'gelFrequency',
    gelFrequency,
    gelFrequencyUnit,
    carbsPerHour,
    fuelSources = [],
    additionalFuelSources = [],
    cutoffTime,
    goalTime,
    startTimeHours = 6,
    startTimeMinutes = 0,
    startTimeAMPM = 'AM'
  } = formData;

  const preRace = getPreRaceFuelState(formData);

  // Ensure we have numeric values for distance
  const distance = parseFloat(totalDistance);
  if (isNaN(distance)) return { segments: [] };

  // Determine units for race and aid stations
  const raceUnit = distanceUnit || 'miles';
  const aidStationUnit = formData.aidStationUnit || 'miles';
  const unitsAreDifferent = raceUnit !== aidStationUnit;
  
  // Validate all aid stations have numeric locations and convert units if needed
  const normalizedAidStations = aidStations.map((station, index) => {
    let locationValue, originalLocationValue, originalUnitValue;
    
    // Parse the location value
    locationValue = typeof station.location === 'number' ? 
      station.location : parseFloat(station.location);
    
    if (isNaN(locationValue)) {
      return null;
    }

    // Get original location from station if available, otherwise use parsed location
    originalLocationValue = station.originalLocation !== undefined ? 
      (typeof station.originalLocation === 'number' ? station.originalLocation : parseFloat(station.originalLocation)) 
      : locationValue;
    
    if (isNaN(originalLocationValue)) {
      originalLocationValue = locationValue;
    }
    
    // Get original unit from station if available, otherwise use aid station unit
    originalUnitValue = station.originalUnit || aidStationUnit;
    
    // If aid station unit is different from race unit, convert the location
    let normalizedLocation = locationValue;
    if (unitsAreDifferent || originalUnitValue !== raceUnit) {
      if (originalUnitValue === 'miles' && raceUnit === 'kilometers') {
        normalizedLocation = convertDistance(originalLocationValue, 'miles', 'kilometers');  // Convert miles to km
      } else if (originalUnitValue === 'kilometers' && raceUnit === 'miles') {
        normalizedLocation = convertDistance(originalLocationValue, 'kilometers', 'miles'); // Convert km to miles
      }
    }
    
    return {
      ...station,
      originalLocation: originalLocationValue,
      originalUnit: originalUnitValue,
      location: normalizedLocation // Now in race units
    };
  }).filter(station => station !== null);

  // Sort aid stations by location
  const sortedAidStations = [...normalizedAidStations].sort((a, b) => a.location - b.location);

  // Prepare list of drop bags
  const dropBags = sortedAidStations.filter(station => station.hasDropBag);

  const nutritionStopHoursTotal = totalPlannedAidStopHours(sortedAidStations);
  
  // Moving pace from goal (goal/cutoff clock minus planned aid stops → min/mile while running)
  let pace = null;
  if (formData.goalTime) {
    const distanceInMiles = distanceUnit === 'miles' 
      ? distance 
      : convertDistance(distance, 'kilometers', 'miles');
    const movingHours = Math.max(MIN_MOVING_HOURS, formData.goalTime - nutritionStopHoursTotal);
    pace = (movingHours * 60) / distanceInMiles;
  }
  
  // Calculate total nutrition needs based on nutrition type
  let totalGels = 0;
  let fuelUnits = [];
  let segments = [];
  
  // Calculate estimated finish time for carbs per hour calculation
  const estimatedFinishTime = formData.goalTime || formData.cutoffTime;
  const distanceInMiles = distanceUnit === 'miles' 
    ? distance 
    : convertDistance(distance, 'kilometers', 'miles');
  
  // Calculate nutrition totals based on type
  if (nutritionType === 'gelFrequency') {
    // Calculate total gels needed for the race using frequency
    const raceGels = calculateGelsNeeded(
      distance, 
      gelFrequency, 
      distanceUnit, 
      gelFrequencyUnit,
      pace
    );
    
    totalGels = preRace.enabled ? raceGels + preRace.gelEquivalents : raceGels;
    
    // Process additional fuel sources if any
    if (additionalFuelSources && additionalFuelSources.length > 0) {
      fuelUnits = additionalFuelSources.map(source => {
        let totalUnits;
        
        // Use source's own frequency if provided, otherwise use gel frequency
        const sourceFrequency = source.frequency || gelFrequency;
        const sourceFrequencyUnit = source.frequencyUnit || gelFrequencyUnit;
        
        if (sourceFrequencyUnit === 'hours') {
          // If frequency is in hours, calculate based on time
          const unitsPerHour = source.unitsPerFrequency / sourceFrequency;
          totalUnits = calculateFuelUnitsNeeded(estimatedFinishTime, unitsPerHour);
        } else {
          // If frequency is in distance, calculate based on distance
          // Calculate how many units needed per frequency interval
          const unitsPerInterval = source.unitsPerFrequency || 1;
          
          // Calculate how many intervals in the total distance
          let intervals;
          
          // Ensure distance and frequency are in the same unit
          if (distanceUnit !== sourceFrequencyUnit) {
            if (distanceUnit === 'miles' && sourceFrequencyUnit === 'kilometers') {
              intervals = distance / convertDistance(sourceFrequency, 'kilometers', 'miles');
            } else if (distanceUnit === 'kilometers' && sourceFrequencyUnit === 'miles') {
              intervals = distance / convertDistance(sourceFrequency, 'miles', 'kilometers');
            }
          } else {
            intervals = distance / sourceFrequency;
          }
          
          totalUnits = Math.ceil(intervals * unitsPerInterval);
        }
        
        return {
          type: source.type,
          name: source.name,
          carbsPerUnit: source.carbsPerUnit,
          frequency: sourceFrequency,
          frequencyUnit: sourceFrequencyUnit,
          unitsPerFrequency: source.unitsPerFrequency || 1,
          displayName: getFuelUnitDisplayName(source.type),
          totalUnits
        };
      });
    }
  } else if (nutritionType === 'carbsPerHour') {
    // Calculate total units needed for each fuel source
    fuelUnits = fuelSources.map(source => {
      const totalUnits = calculateFuelUnitsNeeded(estimatedFinishTime, source.unitsPerHour);
      return {
        type: source.type,
        name: source.name,
        carbsPerUnit: source.carbsPerUnit,
        unitsPerHour: source.unitsPerHour,
        totalUnits,
        displayName: getFuelUnitDisplayName(source.type)
      };
    });
    
    const gelSources = fuelUnits.filter(source => source.type === 'gel');
    const gelsFromSources = gelSources.reduce((total, source) => total + source.totalUnits, 0);
    totalGels = gelsFromSources + (preRace.enabled ? preRace.gelEquivalents : 0);
  }
  
  // Create segments between all aid stations
  let previousLocation = 0;
  let cumulativeTime = 0;
  
  // Find next drop bag location after a given index
  const findNextDropBagLocation = (currentIndex) => {
    for (let i = currentIndex + 1; i < sortedAidStations.length; i++) {
      if (sortedAidStations[i].hasDropBag) {
        return {
          location: sortedAidStations[i].location,
          index: i
        };
      }
    }
    return {
      location: distance,
      index: sortedAidStations.length
    }; // If no more drop bags, use finish line
  };

  // Calculate total distance and time to next drop bag
  // This function determines how far/how long until the next drop bag location,
  // which is critical for nutrition planning because runners need to carry 
  // enough nutrition from one drop bag to the next
  const calculateToNextDropBag = (currentIndex) => {
    const nextDropBag = findNextDropBagLocation(currentIndex);
    const currentLocation = sortedAidStations[currentIndex].location;
    
    // For Drop Bag 0 (Start), we want the full distance to the next drop bag location
    // For other drop bags, we want the distance between the current drop bag and the next
    const totalDistance = nextDropBag.location - (currentIndex === 0 ? 0 : currentLocation);
    
    // Calculate total time to next drop bag
    let totalTime = 0;
    if (nutritionType === 'carbsPerHour') {
      const movingH = Math.max(MIN_MOVING_HOURS, estimatedFinishTime - nutritionStopHoursTotal);
      const hoursPerUnitMoving = movingH / distance;
      totalTime = totalDistance * hoursPerUnitMoving;
    } else if (pace) {
      const distanceInMiles = distanceUnit === 'miles' 
        ? totalDistance 
        : convertDistance(totalDistance, 'kilometers', 'miles');
      totalTime = (distanceInMiles * pace) / 60;
    }
    
    return { totalDistance, totalTime, nextDropBagIndex: nextDropBag.index };
  };

  // Function to calculate nutrition for a segment between drop bags
  const calculateSegmentNutrition = (currentIndex) => {
    // Calculate nutrition needed to reach the next drop bag
    const { totalDistance, totalTime, nextDropBagIndex } = calculateToNextDropBag(currentIndex);

    // For nutrition calculations, we ALWAYS use the distance to the next drop bag
    // This maintains the drop bag to drop bag nutrition logic
    const distanceForNutrition = totalDistance;

    if (nutritionType === 'gelFrequency') {
      const gelsNeeded = calculateGelsNeeded(
        distanceForNutrition,
        gelFrequency,
        distanceUnit,
        gelFrequencyUnit,
        pace
      );

      const segmentFuelUnits = additionalFuelSources && additionalFuelSources.length > 0
        ? additionalFuelSources.map(source => {
            let unitsNeeded;
            const sourceFrequency = source.frequency || gelFrequency;
            const sourceFrequencyUnit = source.frequencyUnit || gelFrequencyUnit;
            
            if (sourceFrequencyUnit === 'hours') {
              const unitsPerHour = source.unitsPerFrequency / sourceFrequency;
              unitsNeeded = calculateFuelUnitsNeeded(totalTime, unitsPerHour);
            } else {
              const unitsPerInterval = source.unitsPerFrequency || 1;
              let intervals;
              
              if (distanceUnit !== sourceFrequencyUnit) {
                if (distanceUnit === 'miles' && sourceFrequencyUnit === 'kilometers') {
                  intervals = distanceForNutrition / convertDistance(sourceFrequency, 'kilometers', 'miles');
                } else if (distanceUnit === 'kilometers' && sourceFrequencyUnit === 'miles') {
                  intervals = distanceForNutrition / convertDistance(sourceFrequency, 'miles', 'kilometers');
                }
              } else {
                intervals = distanceForNutrition / sourceFrequency;
              }
              
              unitsNeeded = Math.ceil(intervals * unitsPerInterval);
            }
            
            return {
              type: source.type,
              name: source.name,
              displayName: getFuelUnitDisplayName(source.type),
              frequency: sourceFrequency,
              frequencyUnit: sourceFrequencyUnit,
              unitsNeeded
            };
          })
        : [];

      return {
        gelsNeeded,
        gelsToCarry: gelsNeeded,
        fuelUnits: segmentFuelUnits,
        noDropBag: false,
        nextDropBagIndex,
        distanceToNextDropBag: totalDistance  // Add this to track actual distance to next drop bag
      };
    } else {
      const segmentFuelUnits = fuelUnits.map(source => {
        const unitsNeeded = Math.ceil(totalTime * source.unitsPerHour);
        return {
          type: source.type,
          name: source.name,
          unitsNeeded,
          displayName: source.displayName
        };
      });

      const segmentGelUnits = segmentFuelUnits.filter(unit => unit.type === 'gel');
      const gelsNeeded = segmentGelUnits.reduce((total, unit) => total + unit.unitsNeeded, 0);

      return {
        gelsNeeded,
        gelsToCarry: gelsNeeded,
        fuelUnits: segmentFuelUnits,
        noDropBag: false,
        nextDropBagIndex,
        distanceToNextDropBag: totalDistance  // Add this to track actual distance to next drop bag
      };
    }
  };

  // Add segments between each aid station
  for (let i = 0; i < sortedAidStations.length; i++) {
    const currentLocation = sortedAidStations[i].location;
    let displayDistance = currentLocation - previousLocation;
    
    // Calculate time for this segment
    let timeInHours = 0;
    if (nutritionType === 'carbsPerHour') {
      const hoursPerUnit = estimatedFinishTime / distance;
      timeInHours = displayDistance * hoursPerUnit;
    } else if (pace) {
      const segmentDistanceInMiles = distanceUnit === 'miles' 
        ? displayDistance 
        : convertDistance(displayDistance, 'kilometers', 'miles');
      timeInHours = (segmentDistanceInMiles * pace) / 60;
    }
    cumulativeTime += timeInHours;

    // Calculate nutrition if this is a drop bag segment
    const isDropBagSegment = i === 0 || sortedAidStations[i].hasDropBag;
    let nutritionInfo;
    
    if (isDropBagSegment) {
      // Calculate nutrition needed to reach next drop bag
      nutritionInfo = calculateSegmentNutrition(i);
      
      // For segment display, we use the actual distance between aid stations
      // The nutrition calculation will still use drop bag to drop bag distance
    } else {
      // For non-drop bag aid stations, show "Drop bag unavailable"
      nutritionInfo = {
        gelsNeeded: 0,
        gelsToCarry: 0,
        fuelUnits: [],
        noDropBag: true
      };
    }

    const endLocationInOriginalUnit = sortedAidStations[i].originalLocation !== undefined ? 
      sortedAidStations[i].originalLocation : sortedAidStations[i].location;

    // Ensure distance and totalDistance are numbers
    const numericDistance = typeof displayDistance === 'number' ? displayDistance : parseFloat(displayDistance) || 0;
    const numericTotalDistance = typeof currentLocation === 'number' ? currentLocation : parseFloat(currentLocation) || 0;

    // Capture the current cumulative time to avoid closure issues in arrow function
    const currentCumulativeTime = cumulativeTime;

    const segmentObject = {
      startLocation: previousLocation,
      endLocation: currentLocation,
      endLocationDisplay: endLocationInOriginalUnit,
      endLocationUnit: sortedAidStations[i].originalUnit || aidStationUnit || 'miles',
      distance: numericDistance,
      totalDistance: numericTotalDistance,
      timeInHours,
      arrivalTimeInHours: cumulativeTime,
      arrivalTimeOfDay: (() => {
        const result = calculateTimeOfDay(startTimeHours, startTimeMinutes, startTimeAMPM, currentCumulativeTime);
        return result;
      })(),
      ...nutritionInfo,
      hasDropBag: isDropBagSegment
    };

    // Add the segment to the array
    segments.push(segmentObject);

    if (!sortedAidStations[i].isFinish) {
      cumulativeTime += parseAidStopMinutes(sortedAidStations[i]) / 60;
    }
    
    previousLocation = currentLocation;
  }
  
  // Add final segment to finish if needed
  if (previousLocation < distance) {
    const finalSegmentDistance = distance - previousLocation;
    
    // Calculate time for final segment
    let timeInHours = 0;
    if (nutritionType === 'carbsPerHour') {
      const hoursPerUnit = estimatedFinishTime / distance;
      timeInHours = finalSegmentDistance * hoursPerUnit;
    } else if (pace) {
      const segmentDistanceInMiles = distanceUnit === 'miles' 
        ? finalSegmentDistance 
        : convertDistance(finalSegmentDistance, 'kilometers', 'miles');
      timeInHours = (segmentDistanceInMiles * pace) / 60;
    }
    cumulativeTime += timeInHours;

    // Ensure final segment distance and total distance are numbers
    const numericFinalDistance = typeof finalSegmentDistance === 'number' ? finalSegmentDistance : parseFloat(finalSegmentDistance) || 0;
    const numericTotalDistance = typeof distance === 'number' ? distance : parseFloat(distance) || 0;

    // Capture the current cumulative time to avoid closure issues in arrow function
    const finalCumulativeTime = cumulativeTime;

    segments.push({
      startLocation: previousLocation,
      endLocation: distance,
      endLocationDisplay: distance,
      endLocationUnit: distanceUnit || 'miles',
      distance: numericFinalDistance,
      totalDistance: numericTotalDistance,
      timeInHours,
      arrivalTimeInHours: cumulativeTime,
      arrivalTimeOfDay: (() => {
        const result = calculateTimeOfDay(startTimeHours, startTimeMinutes, startTimeAMPM, finalCumulativeTime);
        return result;
      })(),
      gelsNeeded: 0,
      gelsToCarry: 0,
      fuelUnits: [],
      hasDropBag: false,
      noDropBag: true
    });
  }
  
  // Calculate drop bag plan (only for aid stations with drop bags)
  const dropBagPlan = dropBags.map((bag, index) => {
    // Find the segment that ends at this drop bag
    const segment = segments.find(s => s.endLocation === bag.location);
    
    return {
      ...bag,
      gelsToAdd: segment ? segment.gelsNeeded : 0,
      fuelUnitsToAdd: segment ? segment.fuelUnits : [],
      arrivalTimeInHours: segment ? segment.arrivalTimeInHours : 0,
      arrivalTimeOfDay: segment ? segment.arrivalTimeOfDay : ''
    };
  });
  
  // Find first segment for startWith calculation
  const firstSegment = segments.find(segment => segment.hasDropBag && segment.startLocation === 0);
  const startWithGels = firstSegment ? firstSegment.gelsToCarry : 0;
  const startWithUnits = firstSegment ? firstSegment.fuelUnits : [];
  
  // BUGFIX: Recalculate totalGels by summing actual segment gels
  // This fixes the rounding discrepancy where ceil() is applied multiple times
  if (nutritionType === 'gelFrequency') {
    const segmentGelsSum = segments.reduce((sum, segment) => {
      return sum + (segment.gelsNeeded || 0);
    }, 0);
    
    totalGels = preRace.enabled ? segmentGelsSum + preRace.gelEquivalents : segmentGelsSum;
  }
  
  // Final return statement
  return {
    nutritionType,
    totalNutritionNeeds: {
      totalGels,
      fuelUnits,
      startWithGels,
      startWithUnits,
      // Calculate total carbs correctly for both strategies
      totalCarbs: nutritionType === 'carbsPerHour' && fuelUnits && Array.isArray(fuelUnits) && fuelUnits.length > 0
        ? fuelUnits.reduce((total, source) => {
            const carbsPerUnit = source.carbsPerUnit || 0;
            const totalUnits = source.totalUnits || 0;
            return total + (carbsPerUnit * totalUnits);
          }, 0)
        : undefined,
      raceTimeInHours: estimatedFinishTime
    },
    segments, // Explicitly include segments array in the return value
    dropBags: dropBagPlan
  };
};

/**
 * Calculates adjusted pace based on terrain grade
 * @param {number} basePace - Base pace in minutes per mile or km
 * @param {number} grade - Terrain grade as a percentage (e.g., 5 for 5% climb)
 * @param {string} paceModel - The pacing model to use ('even', 'terrain', 'simulation')
 * @returns {number} - Adjusted pace in minutes per unit distance
 */
export const calculateGradeAdjustedPace = (basePace, grade, paceModel = 'even') => {
  // If using even splits or grade is invalid, return base pace
  if (paceModel === 'even' || grade === undefined || grade === null || isNaN(grade)) {
    return basePace;
  }
  
  // Implement Naismith's rule as a starting point, adjusted for different models
  if (paceModel === 'terrain') {
    // For uphill: Add approximately +1 min/mile for every 100ft climb per mile (or ~5% grade)
    if (grade > 0) {
      // Convert grade percentage to additional time
      // 5% grade ~= 1 min/mile slower
      return basePace * (1 + (grade * 0.2));
    } 
    // For downhill: Allow some speed increase, but plateaus at steeper descents
    else if (grade < 0) {
      const gradeFactor = Math.min(Math.abs(grade), 10) / 10; // Cap benefit at -10% grade
      // Maximum 20% speed increase on steep downhills
      return basePace * (1 - (gradeFactor * 0.2));
    }
  } 
  else if (paceModel === 'simulation') {
    // More aggressive model that simulates fatigue
    // Uphill is even more punishing, especially in later segments
    if (grade > 0) {
      return basePace * (1 + (grade * 0.25)); // 25% more impact from grade
    } 
    // Downhill provides less benefit due to fatigue/need to protect quads
    else if (grade < 0) {
      const gradeFactor = Math.min(Math.abs(grade), 8) / 8; // Less benefit, capped at -8% grade
      return basePace * (1 - (gradeFactor * 0.15)); // Only 15% max benefit
    }
  }
  
  // Default to base pace if we reach here
  return basePace;
};

/**
 * Distance along the course in race units (miles or km). GPX storage uses cumulative miles in `distance`;
 * `cumulativeDistance` is meters along the track.
 * @param {object} point
 * @param {'miles'|'kilometers'} raceDistanceUnit
 * @returns {number|null}
 */
function elevationPointDistanceAlongCourse(point, raceDistanceUnit) {
  let milesAlong =
    typeof point.distance === 'number' && !Number.isNaN(point.distance) ? point.distance : null;
  if (milesAlong == null && point.cumulativeDistance != null) {
    const m =
      typeof point.cumulativeDistance === 'number'
        ? point.cumulativeDistance
        : parseFloat(point.cumulativeDistance);
    if (!Number.isNaN(m)) milesAlong = m / 1609.34;
  }
  if (milesAlong == null || Number.isNaN(milesAlong)) return null;
  return raceDistanceUnit === 'miles' ? milesAlong : milesAlong * MILES_TO_KM;
}

/**
 * Calculates race pace from target **clock** time: moving pace = distance / (targetHours − planned aid stops).
 * Split arrival times include planned stop time after each aid (finish excluded).
 */
const buildPaceResultForTarget = (formData, paceModel, targetHours, aidStations, distance, distanceUnit) => {
  if (targetHours === undefined || targetHours === null || Number.isNaN(targetHours)) return null;

  const aidStationUnit = formData.aidStationUnit || 'miles';
  const elevationData = formData.elevationData;

  const sortedStations = [...aidStations]
    .filter(station => (station && typeof station.location === 'number') ||
                       (typeof station.location === 'string' && !Number.isNaN(parseFloat(station.location))))
    .map(station => ({
      ...station,
      location: typeof station.location === 'number' ?
        station.location : parseFloat(station.location)
    }))
    .sort((a, b) => a.location - b.location);

  let stationsConverted = sortedStations;
  if (distanceUnit !== aidStationUnit) {
    stationsConverted = sortedStations.map(station => ({
      ...station,
      location: convertDistance(station.location, aidStationUnit, distanceUnit)
    }));
  }

  const normalizedForStops = normalizeAidStations(aidStations, distanceUnit, aidStationUnit);
  const stopHoursTotal = totalPlannedAidStopHours(normalizedForStops);
  const movingHours = Math.max(MIN_MOVING_HOURS, targetHours - stopHoursTotal);
  const mphMoving = distance / movingHours;
  const mileMinutesMoving = 60 / mphMoving;
  const kmMinutesMoving = mileMinutesMoving / MILES_TO_KM;

  if (!aidStations || aidStations.length === 0) {
    return {
      mileMinutes: mileMinutesMoving,
      kmMinutes: kmMinutesMoving,
      finishTime: targetHours,
      stopHoursTotal,
      movingHours,
      averageMphRaceClock: distance / targetHours
    };
  }

  const segmentPoints = [
    { location: 0, isStart: true },
    ...stationsConverted.map(station => ({
      location: station.location,
      name: station.name,
      isAidStation: true,
      isFinish: !!station.isFinish,
      hasDropBag: station.hasDropBag,
      hasPacer: station.hasPacer,
      hasCrew: station.hasCrew,
      stopTimeMinutes: station.stopTimeMinutes,
      aidStopDurationHours: station.aidStopDurationHours,
      aidStopDurationMinutes: station.aidStopDurationMinutes
    })),
    { location: distance, isFinish: true, isAidStation: false }
  ].sort((a, b) => a.location - b.location);

  const rawParts = [];

  for (let i = 0; i < segmentPoints.length - 1; i++) {
    const segmentDistance = segmentPoints[i + 1].location - segmentPoints[i].location;
    if (segmentDistance <= 0) continue;

    let segmentPace = mileMinutesMoving;

    if (elevationData && elevationData.length > 0 && paceModel !== 'even') {
      const segmentStart = segmentPoints[i].location;
      const segmentEnd = segmentPoints[i + 1].location;
      const segmentElevationPoints = elevationData.filter((point) => {
        const along = elevationPointDistanceAlongCourse(point, distanceUnit);
        return along != null && along >= segmentStart && along <= segmentEnd;
      });

      if (segmentElevationPoints.length >= 2) {
        const segLenM =
          distanceUnit === 'miles' ? segmentDistance * 1609.34 : segmentDistance * 1000;

        let totalAscentM = 0;
        for (let j = 1; j < segmentElevationPoints.length; j++) {
          const d = segmentElevationPoints[j].elevation - segmentElevationPoints[j - 1].elevation;
          if (d > 0) totalAscentM += d;
        }

        const elevationGain =
          segmentElevationPoints[segmentElevationPoints.length - 1].elevation -
          segmentElevationPoints[0].elevation;
        const horizontalDistance = segmentDistance;
        let elevationGainInDistanceUnit;
        if (distanceUnit === 'miles') {
          elevationGainInDistanceUnit = elevationGain * METERS_TO_MILES;
        } else {
          elevationGainInDistanceUnit = elevationGain * METERS_TO_KM;
        }
        const netGrade = (elevationGainInDistanceUnit / horizontalDistance) * 100;

        let grade = netGrade;
        if (segLenM > 1 && totalAscentM > 0) {
          const climbGrade = (totalAscentM / segLenM) * 100;
          if (netGrade >= -0.25) {
            grade = Math.max(netGrade, climbGrade * 0.5);
          }
        }

        segmentPace = calculateGradeAdjustedPace(mileMinutesMoving, grade, paceModel);

        if (paceModel === 'simulation' && segmentPoints[i + 1].isAidStation && !segmentPoints[i + 1].isFinish) {
          const aidStationTime = segmentPoints[i + 1].hasDropBag ? 0.2 : 0.1;
          segmentPace = segmentPace * (1 + (aidStationTime / (segmentDistance / mphMoving)));
        }
      }
    }

    const segmentRunTime = segmentDistance * segmentPace / 60;
    rawParts.push({ segmentDistance, segmentPace, segmentRunTime, pointIndex: i + 1 });
  }

  const sumRun = rawParts.reduce((s, p) => s + p.segmentRunTime, 0);
  const scale = sumRun > 1e-8 ? movingHours / sumRun : 1;

  let wallClock = 0;
  const segments = [];

  for (const part of rawParts) {
    const runScaled = part.segmentRunTime * scale;
    wallClock += runScaled;

    const endPoint = segmentPoints[part.pointIndex];
    const displayPace = part.segmentPace * scale;
    const timeOfDay = calculateTimeOfDay(
      parseInt(formData.startTimeHours, 10) || 6,
      parseInt(formData.startTimeMinutes, 10) || 0,
      formData.startTimeAMPM || 'AM',
      wallClock
    );

    segments.push({
      distance: part.segmentDistance,
      totalDistance: endPoint.location,
      pace: displayPace,
      time: runScaled,
      timeInHours: runScaled,
      elapsedTime: wallClock,
      arrivalTimeInHours: wallClock,
      timeOfDay: timeOfDay,
      arrivalTimeOfDay: timeOfDay,
      plannedStopMinutes: endPoint.isAidStation && !endPoint.isFinish ? parseAidStopMinutes(endPoint) : 0,
      ...endPoint
    });

    if (endPoint.isAidStation && !endPoint.isFinish) {
      wallClock += parseAidStopMinutes(endPoint) / 60;
    }
  }

  return {
    mileMinutes: mileMinutesMoving,
    kmMinutes: kmMinutesMoving,
    finishTime: targetHours,
    stopHoursTotal,
    movingHours,
    averageMphRaceClock: distance / targetHours,
    splits: segments,
    segments
  };
};

const calculatePacesInternal = (formData, paceModel = 'even') => {
  const {
    distance: totalDistance,
    distanceUnit,
    cutoffTime,
    goalTime,
    stretchTime,
    aidStations = []
  } = formData;

  const distance = parseFloat(totalDistance);
  if (Number.isNaN(distance) || distance <= 0) return {};

  const result = {};

  if (paceModel !== 'even') {
    console.log('Elevation data for terrain adjustments:', {
      available: !!(formData.elevationData && formData.elevationData.length > 0),
      points: formData.elevationData?.length || 0,
      model: paceModel
    });
  }

  if (cutoffTime != null && !Number.isNaN(cutoffTime)) {
    const built = buildPaceResultForTarget(formData, paceModel, cutoffTime, aidStations, distance, distanceUnit);
    if (built) result.cutoffPace = built;
  }
  if (goalTime != null && !Number.isNaN(goalTime)) {
    const built = buildPaceResultForTarget(formData, paceModel, goalTime, aidStations, distance, distanceUnit);
    if (built) result.goalPace = built;
  }
  if (stretchTime != null && !Number.isNaN(stretchTime)) {
    const built = buildPaceResultForTarget(formData, paceModel, stretchTime, aidStations, distance, distanceUnit);
    if (built) result.stretchPace = built;
  }

  return result;
};

// Create memoized versions for better performance
// These cache results based on form data to avoid recalculating
const memoizedCalculateNutritionPlan = memoize(calculateNutritionPlanInternal, generateFormDataKey);
const memoizedCalculatePaces = memoize(calculatePacesInternal, (formData, paceModel) => {
  return `${generateFormDataKey(formData)}_${paceModel || 'even'}`;
});

// Export memoized versions as the default exports
export { memoizedCalculateNutritionPlan as calculateNutritionPlan };
export { memoizedCalculatePaces as calculatePaces }; 